import type {
  AttestToProductConformityResponse,
  GetProductForManufacturingOrderResponse
} from '@/services/WarrantyApiService/types'
import { ApiError } from '@/services/WarrantyApiService/errors'
import WarrantyApiService from '@/services/WarrantyApiService/WarrantyApiService'
import { useUserStore } from '@/stores/user.store'
import * as Sentry from '@sentry/vue'
import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef, Ref } from 'vue'

const { logger } = Sentry

type ResponseMoProductData = GetProductForManufacturingOrderResponse
type ResponseAttetToConformityData = AttestToProductConformityResponse

export const useAttestToConformity = (input: {
  serialNumber: Ref<string>
  tagCode: Ref<string>
  manufacturingOrderId: string
  productUnitId: string
  activityId: string
  trigger: Ref<'manual' | 'auto-scan-next-tag'>
  duplicatedFlyerConfirmed: Ref<boolean>
}) => {
  const userStore = useUserStore()

  return useQuery<ResponseAttetToConformityData, Error>({
    queryKey: ['attest-to-product-conformit'],
    queryFn: async () => {
      return Sentry.startSpan(
        { name: 'attest-to-product-conformity', op: 'function', forceTransaction: true },
        async (span) => {
          const data = {
            serialNumber: input.serialNumber.value,
            tagCode: input.tagCode.value,
            manufacturingOrderId: input.manufacturingOrderId,
            productUnitId: input.productUnitId,
            activityId: input.activityId,
            ...(input.duplicatedFlyerConfirmed.value ? { duplicatedFlyerConfirmed: true } : {})
          }

          span?.setAttribute('tagCode', data.tagCode)
          span?.setAttribute('manufacturingOrderId', data.manufacturingOrderId)
          span?.setAttribute('activityId', data.activityId)
          span?.setAttribute('trigger', input.trigger.value)

          const response = await WarrantyApiService.attestToProductConformity(data)

          const logData = {
            productionSequenceOperation: userStore.decodedToken?.productionSequenceOperation,
            activity: userStore.decodedToken?.activity,
            accessKey: userStore.decodedToken?.accessKey,
            serialNumber: data.serialNumber,
            tagCode: data.tagCode,
            manufacturingOrderId: data.manufacturingOrderId,
            productUnitId: data.productUnitId
          }

          if (response.isWrong()) {
            const error = new ApiError(response.value.name, response.value.message, response.value.params)

            span?.setAttribute('outcome', 'error')
            span?.setAttribute('errorName', error.name)

            logger.error(`[CONFORMIDADE] - Erro ao apontar produto`, {
              ...logData,
              error: error.message
            })

            throw error
          }

          span?.setAttribute('outcome', 'success')

          logger.info(`[CONFORMIDADE] - Produto apontado`, {
            ...logData
          })

          return response.value
        }
      )
    },
    retry: false,
    enabled: false
  })
}

export async function fetchMoProduct(
  tagCode: Ref<string>,
  manufacturingOrderId: ComputedRef<string | undefined>
): Promise<ResponseMoProductData | Error> {
  const userStore = useUserStore()

  const response = await WarrantyApiService.getProductForManufacturingOrder(
    tagCode.value,
    manufacturingOrderId?.value ?? '',
    userStore.decodedToken?.activity.id ?? ''
  )

  if (response.isWrong()) {
    throw new Error(response.value.message)
  }

  return {
    serialNumber: response.value.serialNumber,
    productUnitId: response.value.productUnitId,
    description: response.value.description,
    size: response.value.size,
    year: response.value.year,
    primaryColorHex: response.value.primaryColorHex,
    secondaryColorHex: response.value.secondaryColorHex,
    loadedAt: response.value.loadedAt ? new Date(response.value.loadedAt) : undefined,
    autoGeneratedSerialNumber: response.value.autoGeneratedSerialNumber
  }
}
